// src/pages/ProjectManager.jsx
//
// King's Academy Faculty Tool Kit — Project Manager (v2: email-keyed identity,
// project-scoped membership, external collaborators).
//
// toolId: project_manager   route: /tools/project-manager
//
// Identity is the EMAIL for everyone (staff + external). Assignment is limited
// to members of the project. External users (email not @kingsacademy.edu.jo)
// cannot create projects, manage membership, or see the staff directory.
//
// Data model:
//   /pmProjects/{projectId}
//       members: [{ email, name, type:'staff'|'external', status:'active'|'pending' }]
//       memberEmails: [email, ...]        // derived; used by rules + queries
//   /pmProjects/{projectId}/tasks/{taskId}
//       assigneeEmail, assigneeName, assigneeType, memberEmails[] (synced server-side)
//   /pmProjects/{projectId}/tasks/{taskId}/comments/{commentId}
//   /pmInvites/{emailLower}               // external invites
//   /pmNotificationsQueue/{id}            // client → server handoff (by email)
//   /users/{uid}/pmNotifications/{id}     // in-app bell
// Files: Storage under project-manager/{projectId}/{taskId}/{filename}

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  collection, collectionGroup, doc, addDoc, updateDoc, deleteDoc, setDoc,
  onSnapshot, query, where, orderBy, serverTimestamp, getDocs, writeBatch,
} from 'firebase/firestore';
import {
  ref as storageRef, uploadBytesResumable, getDownloadURL, deleteObject,
} from 'firebase/storage';
import { db, storage } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';

const KA = { red: '#E83530', navy: '#233C5F', gold: '#A29164' };
const INTERNAL_DOMAIN = '@kingsacademy.edu.jo';

const STATUSES = ['Not Started', 'In Progress', 'Blocked', 'Done', 'Cancelled'];
const STATUS_COLORS = {
  'Not Started': '#9AA5B1', 'In Progress': '#2F80ED', 'Blocked': KA.red,
  'Done': '#27AE60', 'Cancelled': '#6B7280',
};
const PRIORITIES = ['Low', 'Medium', 'High', 'Urgent'];
const PRIORITY_COLORS = { Low: '#9AA5B1', Medium: '#2F80ED', High: '#E2A03F', Urgent: KA.red };

const lc = (s) => (s || '').toLowerCase().trim();
// allowedTools may be an array (['a','b']) or a map ({a:true}); normalize to a list.
const toolsArray = (at) => (Array.isArray(at) ? at : Object.keys(at || {}));

// ---- Amman date helpers (UTC+3, no DST) ------------------------------------
function ammanToday() {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Amman', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date());
}
function daysUntil(dueDate) {
  if (!dueDate) return null;
  const a = new Date(ammanToday() + 'T00:00:00Z');
  const b = new Date(dueDate + 'T00:00:00Z');
  return Math.round((b - a) / 86400000);
}
function dueLabel(dueDate) {
  const d = daysUntil(dueDate);
  if (d === null) return { text: 'No due date', tone: 'muted' };
  if (d < 0) return { text: `Overdue by ${Math.abs(d)}d`, tone: 'over' };
  if (d === 0) return { text: 'Due today', tone: 'today' };
  if (d === 1) return { text: 'Due tomorrow', tone: 'soon' };
  if (d <= 3) return { text: `Due in ${d}d`, tone: 'soon' };
  return { text: `Due ${dueDate}`, tone: 'muted' };
}

// ===========================================================================
export default function ProjectManager() {
  const { user, isSuperAdmin } = useAuth();
  const myEmail = lc(user?.email);
  const isExternal = myEmail && !myEmail.endsWith(INTERNAL_DOMAIN);

  const [myAllowedTools, setMyAllowedTools] = useState([]);
  const [staff, setStaff] = useState([]);           // internal-only assignable pool source
  const [projects, setProjects] = useState([]);
  const [activeProjectId, setActiveProjectId] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [myTasks, setMyTasks] = useState([]);
  const [view, setView] = useState('mine');
  const [loading, setLoading] = useState(true);
  const [openTask, setOpenTask] = useState(null);
  const [showNewProject, setShowNewProject] = useState(false);
  const [showNewTask, setShowNewTask] = useState(false);
  const [showMembers, setShowMembers] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  // ---- current user's own permissions (drives the pmAdmin gate) ----
  useEffect(() => {
    if (!user?.uid) return;
    const unsub = onSnapshot(doc(db, 'users', user.uid), (d) => {
      setMyAllowedTools(d.exists() ? toolsArray(d.data().allowedTools) : []);
    }, (e) => console.warn('own user doc read failed', e));
    return () => unsub();
  }, [user?.uid]);

  // ---- staff directory: only internal users can read /staff ----
  useEffect(() => {
    if (isExternal) { setStaff([]); return; }
    const q = query(collection(db, 'staff'), where('active', '==', 'true'));
    const unsub = onSnapshot(q, (snap) => {
      const rows = []; const seen = new Set();
      snap.forEach((d) => {
        const v = d.data();
        const email = lc(v.email);
        if (!email || seen.has(email)) return;   // dedupe one-row-per-dept + require email
        seen.add(email);
        rows.push({
          email,
          name: v.name || v.fullName || `${v.firstName || ''} ${v.lastName || ''}`.trim() || email,
        });
      });
      rows.sort((a, b) => a.name.localeCompare(b.name));
      setStaff(rows);
    }, (e) => console.warn('staff read denied/failed', e));
    return () => unsub();
  }, [isExternal]);

  // ---- projects: super sees all; everyone else sees projects they're in ----
  useEffect(() => {
    if (!myEmail && !isSuperAdmin) return;
    const base = collection(db, 'pmProjects');
    const q = isSuperAdmin ? base : query(base, where('memberEmails', 'array-contains', myEmail));
    const unsub = onSnapshot(q, (snap) => {
      const rows = []; snap.forEach((d) => rows.push({ id: d.id, ...d.data() }));
      rows.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setProjects(rows);
      setLoading(false);
    }, (e) => { console.error('projects query failed', e); setLoading(false); });
    return () => unsub();
  }, [myEmail, isSuperAdmin]);

  // Keep the selected project valid within the currently visible set. Runs when
  // the project list or the archived toggle changes (incl. right after archive),
  // so the dropdown value never drifts out of sync with what's shown.
  useEffect(() => {
    const vis = projects.filter((p) => showArchived || !p.archived);
    setActiveProjectId((cur) => (cur && vis.some((p) => p.id === cur) ? cur : (vis[0]?.id ?? null)));
  }, [projects, showArchived]);

  // ---- tasks for the active project ----
  useEffect(() => {
    if (!activeProjectId) { setTasks([]); return; }
    const col = collection(db, 'pmProjects', activeProjectId, 'tasks');
    const unsub = onSnapshot(col, (snap) => {
      const rows = []; snap.forEach((d) => rows.push({ id: d.id, projectId: activeProjectId, ...d.data() }));
      setTasks(rows);
    });
    return () => unsub();
  }, [activeProjectId]);

  // ---- "My Tasks" across all projects, authorized by assigneeEmail ----
  useEffect(() => {
    if (!myEmail) { setMyTasks([]); return; }
    const q = query(collectionGroup(db, 'tasks'), where('assigneeEmail', '==', myEmail));
    const unsub = onSnapshot(q, (snap) => {
      const rows = [];
      snap.forEach((d) => rows.push({ id: d.id, projectId: d.ref.parent.parent?.id, ...d.data() }));
      setMyTasks(rows);
    }, (err) => console.error('myTasks needs a collectionGroup index on tasks.assigneeEmail:', err));
    return () => unsub();
  }, [myEmail]);

  // Derived dependency-blocking (recompute on read, never stored).
  const blockedMap = useMemo(() => {
    const byId = {}; tasks.forEach((t) => { byId[t.id] = t; });
    const map = {};
    tasks.forEach((t) => {
      map[t.id] = (t.dependsOn || []).filter((id) => {
        const dep = byId[id];
        return dep && dep.status !== 'Done' && dep.status !== 'Cancelled';
      });
    });
    return map;
  }, [tasks]);

  // pmAdmin = can create/own projects. Externals can never hold this capability.
  const isPmAdmin = !isExternal && (isSuperAdmin || myAllowedTools.includes('project_manager_admin'));

  const activeProject = projects.find((p) => p.id === activeProjectId) || null;
  const activeMembers = (activeProject?.members || []).filter((m) => m.status !== 'removed');
  const canManage = !!activeProject && !isExternal &&
    (isSuperAdmin || lc(activeProject.ownerEmail) === myEmail);

  // ---- task mutations ----
  const patchTask = useCallback(async (task, patch) => {
    const tref = doc(db, 'pmProjects', task.projectId, 'tasks', task.id);
    await updateDoc(tref, { ...patch, updatedAt: serverTimestamp(), updatedByEmail: myEmail });
    if (patch.assigneeEmail && patch.assigneeEmail !== task.assigneeEmail) {
      await enqueue(patch.assigneeEmail, task.projectId, task.id, task.title, 'assigned');
    }
  }, [myEmail]);

  const setStatus = useCallback((task, status) => patchTask(task, { status }), [patchTask]);

  const deleteTask = useCallback(async (task) => {
    if (!window.confirm(`Delete task "${task.title}"?`)) return;
    const batch = writeBatch(db);
    tasks.forEach((t) => {
      if ((t.dependsOn || []).includes(task.id)) {
        batch.update(doc(db, 'pmProjects', task.projectId, 'tasks', t.id),
          { dependsOn: (t.dependsOn || []).filter((x) => x !== task.id) });
      }
    });
    batch.delete(doc(db, 'pmProjects', task.projectId, 'tasks', task.id));
    await batch.commit();
    setOpenTask(null);
  }, [tasks]);

  // ---- project archive (soft) + delete (hard, cascades) ----
  const archiveProject = useCallback(async (project, archived) => {
    // Just flip the flag — the reconcile effect keeps the selection valid, so
    // archiving with "Show archived" on keeps it selected (ready to Unarchive),
    // and with it off it falls back to the next visible project.
    await updateDoc(doc(db, 'pmProjects', project.id), { archived });
  }, []);

  const deleteProject = useCallback(async (project) => {
    if (!window.confirm(
      `Permanently delete "${project.name}" and ALL of its tasks, comments, and uploaded files?\n\nThis cannot be undone. (To hide it instead, use Archive.)`
    )) return;
    // Cascade: subcollections aren't auto-deleted, so remove tasks + their
    // comments + Storage files first, then the project doc.
    const tasksSnap = await getDocs(collection(db, 'pmProjects', project.id, 'tasks'));
    for (const t of tasksSnap.docs) {
      const cSnap = await getDocs(collection(db, 'pmProjects', project.id, 'tasks', t.id, 'comments'));
      if (!cSnap.empty) {
        let batch = writeBatch(db); let n = 0;
        for (const c of cSnap.docs) {
          batch.delete(c.ref);
          if (++n === 400) { await batch.commit(); batch = writeBatch(db); n = 0; }
        }
        if (n > 0) await batch.commit();
      }
      for (const f of (t.data().files || [])) {
        try { await deleteObject(storageRef(storage, f.path)); } catch (e) { /* already gone */ }
      }
      await deleteDoc(t.ref);
    }
    await deleteDoc(doc(db, 'pmProjects', project.id));
    setActiveProjectId(null);
  }, []);

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Loading Project Manager…</div>;

  return (
    <div style={S.wrap}>
      <header style={S.header}>
        <div>
          <h1 style={S.h1}>Project Manager</h1>
          <div style={S.sub}>
            {isExternal ? 'External collaborator view' : 'Tasks, dependencies & progress'}
          </div>
        </div>
        <NotificationBell uid={user?.uid} onOpen={(n) => {
          if (n.projectId) { setActiveProjectId(n.projectId); setView('project'); }
        }} />
      </header>

      <nav style={S.tabs}>
        <Tab active={view === 'mine'} onClick={() => setView('mine')}>My Tasks</Tab>
        <Tab active={view === 'project'} onClick={() => setView('project')}>Project Board</Tab>
        {!isExternal && <Tab active={view === 'overview'} onClick={() => setView('overview')}>Manager Overview</Tab>}
      </nav>

      {view === 'mine' && (
        <MyTasksView myTasks={myTasks} projects={projects}
          onOpen={(t) => { setActiveProjectId(t.projectId); setOpenTask(t); }} onStatus={setStatus} />
      )}

      {view === 'project' && (
        <>
          <div style={S.projectBar}>
            <select value={activeProjectId || ''} onChange={(e) => setActiveProjectId(e.target.value)} style={S.select}>
              {projects.filter((p) => showArchived || !p.archived).length === 0 && <option value="">No projects yet</option>}
              {projects.filter((p) => showArchived || !p.archived).map((p) =>
                <option key={p.id} value={p.id}>{p.name}{p.archived ? ' (archived)' : ''}</option>)}
            </select>
            {isPmAdmin && <button style={S.btnGhost} onClick={() => setShowNewProject(true)}>+ New Project</button>}
            {canManage && <button style={S.btnGhost} onClick={() => setShowMembers(true)}>Members ({activeMembers.length})</button>}
            {canManage && activeProject && (activeProject.archived
              ? <button style={S.btnGhost} onClick={() => archiveProject(activeProject, false)}>Unarchive</button>
              : <button style={S.btnGhost} onClick={() => archiveProject(activeProject, true)}>Archive</button>)}
            {canManage && activeProject && (
              <button style={{ ...S.btnGhost, color: KA.red, borderColor: KA.red }} onClick={() => deleteProject(activeProject)}>Delete</button>
            )}
            {activeProject && !activeProject.archived && <button style={S.btnPrimary} onClick={() => setShowNewTask(true)}>+ New Task</button>}
            <label style={{ fontSize: 12, color: '#666', display: 'flex', alignItems: 'center', gap: 4, marginLeft: 'auto' }}>
              <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
              Show archived
            </label>
          </div>

          {activeProject && (
            <ProjectBoard project={activeProject} tasks={tasks} blockedMap={blockedMap}
              onOpen={setOpenTask} />
          )}
        </>
      )}

      {view === 'overview' && !isExternal && (
        <ManagerOverview projects={projects.filter((p) => !p.archived)}
          onOpenProject={(id) => { setActiveProjectId(id); setView('project'); }} />
      )}

      {openTask && (
        <TaskDrawer
          task={tasks.find((t) => t.id === openTask.id) || openTask}
          project={activeProject} allTasks={tasks} blockedMap={blockedMap}
          members={activeMembers} user={user} myEmail={myEmail}
          onClose={() => setOpenTask(null)} onPatch={patchTask} onDelete={deleteTask} />
      )}

      {showNewProject && isPmAdmin && (
        <NewProjectModal staff={staff} user={user} myEmail={myEmail}
          onClose={() => setShowNewProject(false)}
          onCreated={(id) => { setActiveProjectId(id); setView('project'); setShowNewProject(false); }} />
      )}

      {showNewTask && activeProject && (
        <NewTaskModal project={activeProject} members={activeMembers} existingTasks={tasks}
          user={user} myEmail={myEmail} onClose={() => setShowNewTask(false)} />
      )}

      {showMembers && canManage && (
        <MembersModal project={activeProject} staff={staff} user={user}
          onClose={() => setShowMembers(false)} />
      )}
    </div>
  );
}

// ---- queue a notification (server resolves email→uid, sends email) ---------
async function enqueue(assigneeEmail, projectId, taskId, title, kind) {
  try {
    await addDoc(collection(db, 'pmNotificationsQueue'), {
      assigneeEmail: lc(assigneeEmail), projectId, taskId, title, kind,
      createdAt: serverTimestamp(), processed: false,
    });
  } catch (e) { console.error('enqueue failed', e); }
}

// ---- membership helpers ----------------------------------------------------
function membersToEmails(members) {
  return Array.from(new Set((members || []).filter((m) => m.status !== 'removed').map((m) => lc(m.email))));
}

// ===========================================================================
// Members modal — add staff (from directory) or invite external by email.
// ===========================================================================
function MembersModal({ project, staff, user, onClose }) {
  const [members, setMembers] = useState(project.members || []);
  const [pickStaff, setPickStaff] = useState('');
  const [extEmail, setExtEmail] = useState('');
  const [extName, setExtName] = useState('');
  const [busy, setBusy] = useState(false);

  const save = async (nextMembers) => {
    setBusy(true);
    await updateDoc(doc(db, 'pmProjects', project.id), {
      members: nextMembers,
      memberEmails: membersToEmails(nextMembers),
    });
    setMembers(nextMembers);
    setBusy(false);
  };

  const addStaff = async () => {
    const s = staff.find((x) => x.email === pickStaff);
    if (!s || members.some((m) => lc(m.email) === s.email)) return;
    await save([...members, { email: s.email, name: s.name, type: 'staff', status: 'active' }]);
    setPickStaff('');
  };

  const inviteExternal = async () => {
    const email = lc(extEmail);
    if (!email || members.some((m) => lc(m.email) === email)) return;
    // 1) write/merge the invite (keyed by email) so the blocking function lets them in
    const inviteRef = doc(db, 'pmInvites', email);
    const existing = await getDocs(query(collection(db, 'pmInvites'), where('email', '==', email)));
    const priorProjectIds = existing.docs[0]?.data()?.projectIds || [];
    await setDoc(inviteRef, {
      email, name: extName || email,
      invitedByUid: user?.uid || null, invitedByEmail: lc(user?.email),
      projectIds: Array.from(new Set([...priorProjectIds, project.id])),
      revoked: false, createdAt: serverTimestamp(),
    }, { merge: true });
    // 2) add them as a pending member (works immediately; flips to active on first sign-in)
    await save([...members, { email, name: extName || email, type: 'external', status: 'pending' }]);
    setExtEmail(''); setExtName('');
  };

  const removeMember = async (email) => {
    if (lc(email) === lc(project.ownerEmail)) { window.alert("Can't remove the project owner."); return; }
    await save(members.filter((m) => lc(m.email) !== lc(email)));
  };

  const staffOptions = staff.filter((s) => !members.some((m) => lc(m.email) === s.email));

  return (
    <Modal title={`Members — ${project.name}`} onClose={onClose}>
      <div style={{ marginBottom: 14 }}>
        {members.map((m) => (
          <div key={m.email} style={S.fileRow}>
            <span style={{ flex: 1 }}>
              {m.name} <span style={{ color: '#999', fontSize: 12 }}>· {m.email}</span>
            </span>
            <span style={{ ...S.tag, background: m.type === 'external' ? KA.gold : KA.navy }}>
              {m.type}{m.status === 'pending' ? ' · pending' : ''}
            </span>
            <button style={S.linkDanger} onClick={() => removeMember(m.email)}>remove</button>
          </div>
        ))}
      </div>

      <div style={S.sectionLabel}>Add staff</div>
      <div style={{ display: 'flex', gap: 8 }}>
        <select style={S.select} value={pickStaff} onChange={(e) => setPickStaff(e.target.value)}>
          <option value="">Choose from directory…</option>
          {staffOptions.map((s) => <option key={s.email} value={s.email}>{s.name}</option>)}
        </select>
        <button style={S.btnGhost} disabled={busy || !pickStaff} onClick={addStaff}>Add</button>
      </div>

      <div style={{ ...S.sectionLabel, marginTop: 16 }}>Invite external collaborator</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 8 }}>
        <input style={S.select} placeholder="Name" value={extName} onChange={(e) => setExtName(e.target.value)} />
        <input style={S.select} placeholder="email@example.com" value={extEmail} onChange={(e) => setExtEmail(e.target.value)} />
        <button style={S.btnPrimary} disabled={busy || !extEmail} onClick={inviteExternal}>Invite</button>
      </div>
      <p style={{ fontSize: 12, color: '#777', marginTop: 8 }}>
        They'll get an email inviting them to sign in (Google or an email link) with this address. Once they do,
        this project appears on their dashboard automatically.
      </p>
    </Modal>
  );
}

// ---- Notification bell -----------------------------------------------------
function NotificationBell({ uid, onOpen }) {
  const [notifs, setNotifs] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!uid) return;
    const q = query(collection(db, 'users', uid, 'pmNotifications'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      const rows = []; snap.forEach((d) => rows.push({ id: d.id, ...d.data() }));
      setNotifs(rows.slice(0, 30));
    });
    return () => unsub();
  }, [uid]);
  const unread = notifs.filter((n) => !n.read).length;
  const markRead = (n) => updateDoc(doc(db, 'users', uid, 'pmNotifications', n.id), { read: true });
  return (
    <div style={{ position: 'relative' }}>
      <button style={S.bell} onClick={() => setOpen((o) => !o)}>
        🔔{unread > 0 && <span style={S.badge}>{unread}</span>}
      </button>
      {open && (
        <div style={S.bellPanel}>
          {notifs.length === 0 && <div style={{ padding: 12, color: '#888' }}>No notifications</div>}
          {notifs.map((n) => (
            <div key={n.id} style={{ ...S.notifRow, fontWeight: n.read ? 400 : 600 }}
              onClick={() => { markRead(n); onOpen(n); setOpen(false); }}>
              <div>{n.message || `${n.kind}: ${n.title}`}</div>
              <div style={{ fontSize: 11, color: '#999' }}>{fmtTs(n.createdAt)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---- My Tasks --------------------------------------------------------------
function MyTasksView({ myTasks, projects, onOpen, onStatus }) {
  const projName = (id) => projects.find((p) => p.id === id)?.name || '—';
  const active = myTasks.filter((t) => t.status !== 'Done' && t.status !== 'Cancelled');
  const done = myTasks.filter((t) => t.status === 'Done');
  const sorted = [...active].sort((a, b) => {
    const da = daysUntil(a.dueDate); const dbb = daysUntil(b.dueDate);
    if (da === null) return 1; if (dbb === null) return -1; return da - dbb;
  });
  return (
    <div style={S.panel}>
      <h2 style={S.h2}>My Open Tasks ({active.length})</h2>
      {sorted.length === 0 && <div style={{ color: '#888' }}>Nothing assigned to you. 🎉</div>}
      {sorted.map((t) => {
        const dl = dueLabel(t.dueDate);
        return (
          <div key={`${t.projectId}-${t.id}`} style={S.myRow} onClick={() => onOpen(t)}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600 }}>{t.title}</div>
              <div style={{ fontSize: 12, color: '#666' }}>{projName(t.projectId)}</div>
            </div>
            <span style={{ ...S.due, ...dueTone(dl.tone) }}>{dl.text}</span>
            <StatusPill status={t.status} />
            <select value={t.status} onClick={(e) => e.stopPropagation()}
              onChange={(e) => onStatus(t, e.target.value)} style={S.miniSelect}>
              {STATUSES.map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
        );
      })}
      {done.length > 0 && (
        <details style={{ marginTop: 20 }}>
          <summary style={{ cursor: 'pointer', color: '#666' }}>Completed ({done.length})</summary>
          {done.map((t) => (
            <div key={`${t.projectId}-${t.id}`} style={{ ...S.myRow, opacity: 0.6 }} onClick={() => onOpen(t)}>
              <div style={{ flex: 1, textDecoration: 'line-through' }}>{t.title}</div>
              <div style={{ fontSize: 12, color: '#666' }}>{projName(t.projectId)}</div>
            </div>
          ))}
        </details>
      )}
    </div>
  );
}

// ---- Project board ---------------------------------------------------------
function ProjectBoard({ project, tasks, blockedMap, onOpen }) {
  const cols = ['Not Started', 'In Progress', 'Blocked', 'Done'];
  const grouped = useMemo(() => {
    const g = {}; cols.forEach((c) => { g[c] = []; });
    tasks.forEach((t) => {
      if (t.status === 'Cancelled') return;
      const derivedBlocked = (blockedMap[t.id] || []).length > 0 && t.status !== 'Done';
      const bucket = derivedBlocked ? 'Blocked' : (g[t.status] ? t.status : 'Not Started');
      (g[bucket] || g['Not Started']).push(t);
    });
    return g;
  }, [tasks, blockedMap]);

  const progress = useMemo(() => {
    const live = tasks.filter((t) => t.status !== 'Cancelled');
    const done = live.filter((t) => t.status === 'Done').length;
    return live.length ? Math.round((done / live.length) * 100) : 0;
  }, [tasks]);

  return (
    <div style={S.panel}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
        <h2 style={{ ...S.h2, margin: 0 }}>{project.name}</h2>
        <div style={S.progressWrap}><div style={{ ...S.progressBar, width: `${progress}%` }} /></div>
        <span style={{ fontSize: 13, color: '#666' }}>{progress}% done</span>
      </div>
      {project.description && <p style={{ color: '#555', marginTop: 0 }}>{project.description}</p>}
      <div style={S.board}>
        {cols.map((c) => (
          <div key={c} style={S.column}>
            <div style={{ ...S.colHead, color: STATUS_COLORS[c] }}>{c} <span style={S.colCount}>{grouped[c].length}</span></div>
            {grouped[c].map((t) => {
              const unmet = blockedMap[t.id] || [];
              const dl = dueLabel(t.dueDate);
              return (
                <div key={t.id} style={S.card} onClick={() => onOpen(t)}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6 }}>
                    <span style={{ fontWeight: 600, fontSize: 14 }}>{t.title}</span>
                    {t.priority && t.priority !== 'Medium' &&
                      <span style={{ ...S.prio, background: PRIORITY_COLORS[t.priority] }}>{t.priority}</span>}
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}>
                    <span style={{ fontSize: 12, color: '#555' }}>{t.assigneeName || 'Unassigned'}</span>
                    <span style={{ ...S.due, ...dueTone(dl.tone), marginLeft: 'auto' }}>{dl.text}</span>
                  </div>
                  {unmet.length > 0 && <div style={S.blockedNote}>⛔ Waiting on {unmet.length} task{unmet.length > 1 ? 's' : ''}</div>}
                  {t.fileCount > 0 && <div style={S.fileChip}>📎 {t.fileCount}</div>}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

// ---- Manager overview ------------------------------------------------------
function ManagerOverview({ projects, onOpenProject }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const out = [];
      for (const p of projects) {
        const snap = await getDocs(collection(db, 'pmProjects', p.id, 'tasks'));
        let total = 0, done = 0, overdue = 0, blocked = 0;
        snap.forEach((d) => {
          const t = d.data(); if (t.status === 'Cancelled') return;
          total++;
          if (t.status === 'Done') done++;
          else {
            const du = daysUntil(t.dueDate);
            if (du !== null && du < 0) overdue++;
            if (t.status === 'Blocked') blocked++;
          }
        });
        out.push({ ...p, total, done, overdue, blocked });
      }
      if (!cancelled) setRows(out);
    })();
    return () => { cancelled = true; };
  }, [projects]);
  return (
    <div style={S.panel}>
      <h2 style={S.h2}>Manager Overview</h2>
      {rows.length === 0 && <div style={{ color: '#888' }}>No projects to show.</div>}
      {rows.map((r) => {
        const pct = r.total ? Math.round((r.done / r.total) * 100) : 0;
        return (
          <div key={r.id} style={S.ovRow} onClick={() => onOpenProject(r.id)}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600 }}>{r.name}</div>
              <div style={S.progressWrap}><div style={{ ...S.progressBar, width: `${pct}%` }} /></div>
            </div>
            <div style={S.ovStat}>{pct}%</div>
            <div style={{ ...S.ovStat, color: r.overdue ? KA.red : '#999' }}>{r.overdue} overdue</div>
            <div style={{ ...S.ovStat, color: r.blocked ? '#E2A03F' : '#999' }}>{r.blocked} blocked</div>
            <div style={S.ovStat}>{r.done}/{r.total} done</div>
          </div>
        );
      })}
    </div>
  );
}

// ---- Task drawer -----------------------------------------------------------
function TaskDrawer({ task, project, allTasks, blockedMap, members, user, myEmail, onClose, onPatch, onDelete }) {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [files, setFiles] = useState(task.files || []);
  const [uploadPct, setUploadPct] = useState(null);
  const [editTitle, setEditTitle] = useState(task.title);
  const fileInput = useRef(null);

  useEffect(() => {
    const q = query(collection(db, 'pmProjects', task.projectId, 'tasks', task.id, 'comments'), orderBy('createdAt', 'asc'));
    const unsub = onSnapshot(q, (snap) => {
      const rows = []; snap.forEach((d) => rows.push({ id: d.id, ...d.data() })); setComments(rows);
    });
    return () => unsub();
  }, [task.projectId, task.id]);
  useEffect(() => { setFiles(task.files || []); setEditTitle(task.title); }, [task.id, task.files, task.title]);

  const unmet = blockedMap[task.id] || [];
  const depOptions = allTasks.filter((t) => t.id !== task.id);

  const setAssignee = (email) => {
    const m = members.find((x) => lc(x.email) === lc(email));
    onPatch(task, {
      assigneeEmail: email ? lc(email) : null,
      assigneeName: m?.name || null,
      assigneeType: m?.type || null,
    });
  };

  const addComment = async () => {
    const text = newComment.trim(); if (!text) return;
    await addDoc(collection(db, 'pmProjects', task.projectId, 'tasks', task.id, 'comments'), {
      text, authorEmail: myEmail, authorName: user?.displayName || user?.email || 'Unknown', createdAt: serverTimestamp(),
    });
    setNewComment('');
    if (task.assigneeEmail && task.assigneeEmail !== myEmail) {
      await enqueue(task.assigneeEmail, task.projectId, task.id, task.title, 'comment');
    }
  };

  const startEdit = (c) => { setEditingId(c.id); setEditText(c.text); };
  const cancelEdit = () => { setEditingId(null); setEditText(''); };
  const saveEdit = async (c) => {
    const text = editText.trim(); if (!text) return;
    await updateDoc(doc(db, 'pmProjects', task.projectId, 'tasks', task.id, 'comments', c.id),
      { text, editedAt: serverTimestamp() });
    cancelEdit();
  };
  const deleteComment = async (c) => {
    if (!window.confirm('Delete this comment?')) return;
    await deleteDoc(doc(db, 'pmProjects', task.projectId, 'tasks', task.id, 'comments', c.id));
  };

  const onPickFiles = async (e) => {
    const chosen = Array.from(e.target.files || []);
    for (const f of chosen) {
      const path = `project-manager/${task.projectId}/${task.id}/${Date.now()}_${f.name}`;
      const up = uploadBytesResumable(storageRef(storage, path), f);
      await new Promise((resolve, reject) => {
        up.on('state_changed',
          (s) => setUploadPct(Math.round((s.bytesTransferred / s.totalBytes) * 100)),
          reject,
          async () => {
            const url = await getDownloadURL(up.snapshot.ref);
            const next = [...(task.files || []), { name: f.name, path, url, size: f.size, uploadedAt: Date.now(), byEmail: myEmail }];
            await onPatch(task, { files: next, fileCount: next.length });
            resolve();
          });
      });
    }
    setUploadPct(null);
    if (fileInput.current) fileInput.current.value = '';
  };

  const removeFile = async (entry) => {
    if (!window.confirm(`Remove ${entry.name}?`)) return;
    try { await deleteObject(storageRef(storage, entry.path)); } catch (e) { /* gone */ }
    const next = (task.files || []).filter((f) => f.path !== entry.path);
    await onPatch(task, { files: next, fileCount: next.length });
  };

  return (
    <div style={S.drawerOverlay} onClick={onClose}>
      <div style={S.drawer} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <input style={S.titleInput} value={editTitle} onChange={(e) => setEditTitle(e.target.value)}
            onBlur={() => editTitle !== task.title && onPatch(task, { title: editTitle })} />
          <button style={S.closeBtn} onClick={onClose}>✕</button>
        </div>

        {unmet.length > 0 && (
          <div style={S.warnBanner}>⛔ Blocked — waiting on: {unmet.map((id) => allTasks.find((t) => t.id === id)?.title || id).join(', ')}</div>
        )}

        <div style={S.fieldGrid}>
          <Field label="Assignee (project members)">
            <select value={task.assigneeEmail || ''} onChange={(e) => setAssignee(e.target.value)} style={S.select}>
              <option value="">Unassigned</option>
              {members.map((m) => <option key={m.email} value={m.email}>{m.name}</option>)}
            </select>
          </Field>
          <Field label="Status">
            <select value={task.status} onChange={(e) => onPatch(task, { status: e.target.value })} style={S.select}>
              {STATUSES.map((s) => <option key={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Due date">
            <input type="date" value={task.dueDate || ''} onChange={(e) => onPatch(task, { dueDate: e.target.value })} style={S.select} />
          </Field>
          <Field label="Priority">
            <select value={task.priority || 'Medium'} onChange={(e) => onPatch(task, { priority: e.target.value })} style={S.select}>
              {PRIORITIES.map((p) => <option key={p}>{p}</option>)}
            </select>
          </Field>
        </div>

        <Field label="Description">
          <textarea style={S.textarea} defaultValue={task.description || ''}
            onBlur={(e) => e.target.value !== (task.description || '') && onPatch(task, { description: e.target.value })}
            placeholder="Add details…" />
        </Field>

        <Field label="Depends on (must be Done first)">
          <MultiSelect options={depOptions.map((t) => ({ value: t.id, label: t.title }))}
            selected={task.dependsOn || []} onChange={(vals) => onPatch(task, { dependsOn: vals })} />
        </Field>

        <div style={{ marginTop: 16 }}>
          <div style={S.sectionLabel}>Files</div>
          {files.map((f) => (
            <div key={f.path} style={S.fileRow}>
              <a href={f.url} target="_blank" rel="noreferrer" style={{ color: KA.navy, flex: 1 }}>📎 {f.name}</a>
              <span style={{ fontSize: 11, color: '#999' }}>{(f.size / 1024).toFixed(0)} KB</span>
              <button style={S.linkDanger} onClick={() => removeFile(f)}>remove</button>
            </div>
          ))}
          <input ref={fileInput} type="file" multiple onChange={onPickFiles} style={{ marginTop: 8 }} />
          {uploadPct !== null && <div style={{ fontSize: 12, color: '#666' }}>Uploading… {uploadPct}%</div>}
        </div>

        <div style={{ marginTop: 20 }}>
          <div style={S.sectionLabel}>Comments</div>
          {comments.map((c) => {
            const mine = lc(c.authorEmail) === myEmail;
            const isEditing = editingId === c.id;
            return (
              <div key={c.id} style={S.comment}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: KA.navy }}>{c.authorName}</div>
                  {mine && !isEditing && (
                    <div style={{ display: 'flex', gap: 10 }}>
                      <button style={S.linkBtn} onClick={() => startEdit(c)}>edit</button>
                      <button style={S.linkDanger} onClick={() => deleteComment(c)}>delete</button>
                    </div>
                  )}
                </div>
                {isEditing ? (
                  <div style={{ marginTop: 4 }}>
                    <textarea style={{ ...S.textarea, minHeight: 44 }} value={editText}
                      onChange={(e) => setEditText(e.target.value)} autoFocus />
                    <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                      <button style={S.btnPrimary} onClick={() => saveEdit(c)}>Save</button>
                      <button style={S.btnGhost} onClick={cancelEdit}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div style={{ fontSize: 14, whiteSpace: 'pre-wrap' }}>{c.text}</div>
                )}
                <div style={{ fontSize: 11, color: '#999' }}>{fmtTs(c.createdAt)}{c.editedAt ? ' · edited' : ''}</div>
              </div>
            );
          })}
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <textarea style={{ ...S.textarea, minHeight: 40 }} value={newComment}
              onChange={(e) => setNewComment(e.target.value)} placeholder="Write a comment…"
              onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) addComment(); }} />
            <button style={S.btnPrimary} onClick={addComment}>Post</button>
          </div>
        </div>

        <div style={{ marginTop: 24, display: 'flex', justifyContent: 'space-between' }}>
          <button style={S.linkDanger} onClick={() => onDelete(task)}>Delete task</button>
          <span style={{ fontSize: 11, color: '#aaa' }}>Updated {fmtTs(task.updatedAt)}</span>
        </div>
      </div>
    </div>
  );
}

// ---- New project -----------------------------------------------------------
function NewProjectModal({ staff, user, myEmail, onClose, onCreated }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [picked, setPicked] = useState([]); // staff emails
  const [saving, setSaving] = useState(false);
  const myName = user?.displayName || myEmail;

  const save = async () => {
    if (!name.trim()) return;
    setSaving(true);
    const members = [
      { email: myEmail, name: myName, type: 'staff', status: 'active' }, // owner is always a member
      ...staff.filter((s) => picked.includes(s.email) && s.email !== myEmail)
        .map((s) => ({ email: s.email, name: s.name, type: 'staff', status: 'active' })),
    ];
    const refDoc = await addDoc(collection(db, 'pmProjects'), {
      name: name.trim(), description: description.trim(),
      ownerUid: user?.uid || null, ownerEmail: myEmail,
      members, memberEmails: membersToEmails(members),
      createdAt: serverTimestamp(), createdByUid: user?.uid || null,
    });
    setSaving(false); onCreated(refDoc.id);
  };
  return (
    <Modal title="New Project" onClose={onClose}>
      <Field label="Project name"><input style={S.select} value={name} onChange={(e) => setName(e.target.value)} autoFocus /></Field>
      <Field label="Description"><textarea style={S.textarea} value={description} onChange={(e) => setDescription(e.target.value)} /></Field>
      <Field label="Add staff members (invite externals later from Members)">
        <CheckboxDropdown
          options={staff.map((s) => ({ value: s.email, label: s.name }))}
          selected={picked}
          onChange={setPicked}
          placeholder="Choose staff to participate…"
        />
      </Field>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
        <button style={S.btnGhost} onClick={onClose}>Cancel</button>
        <button style={S.btnPrimary} disabled={saving} onClick={save}>Create</button>
      </div>
    </Modal>
  );
}

// ---- New task --------------------------------------------------------------
function NewTaskModal({ project, members, existingTasks, user, myEmail, onClose }) {
  const [title, setTitle] = useState('');
  const [assignee, setAssignee] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [priority, setPriority] = useState('Medium');
  const [dependsOn, setDependsOn] = useState([]);
  const [recurrence, setRecurrence] = useState('none');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!title.trim()) return;
    setSaving(true);
    const m = members.find((x) => lc(x.email) === lc(assignee));
    const tref = await addDoc(collection(db, 'pmProjects', project.id, 'tasks'), {
      title: title.trim(), description: '',
      assigneeEmail: assignee ? lc(assignee) : null,
      assigneeName: m?.name || null, assigneeType: m?.type || null,
      status: 'Not Started', dueDate: dueDate || null, priority, dependsOn, recurrence,
      memberEmails: project.memberEmails || [],           // seeded; server keeps in sync
      watchers: [lc(project.ownerEmail)].filter(Boolean),
      files: [], fileCount: 0,
      createdAt: serverTimestamp(), createdByEmail: myEmail, updatedAt: serverTimestamp(),
    });
    if (assignee) await enqueue(assignee, project.id, tref.id, title.trim(), 'assigned');
    setSaving(false); onClose();
  };
  return (
    <Modal title="New Task" onClose={onClose}>
      <Field label="Title"><input style={S.select} value={title} onChange={(e) => setTitle(e.target.value)} autoFocus /></Field>
      <div style={S.fieldGrid}>
        <Field label="Assignee">
          <select style={S.select} value={assignee} onChange={(e) => setAssignee(e.target.value)}>
            <option value="">Unassigned</option>
            {members.map((m) => <option key={m.email} value={m.email}>{m.name}</option>)}
          </select>
        </Field>
        <Field label="Due date"><input type="date" style={S.select} value={dueDate} onChange={(e) => setDueDate(e.target.value)} /></Field>
        <Field label="Priority">
          <select style={S.select} value={priority} onChange={(e) => setPriority(e.target.value)}>{PRIORITIES.map((p) => <option key={p}>{p}</option>)}</select>
        </Field>
        <Field label="Repeats">
          <select style={S.select} value={recurrence} onChange={(e) => setRecurrence(e.target.value)}>
            <option value="none">Does not repeat</option><option value="daily">Daily</option>
            <option value="weekly">Weekly</option><option value="monthly">Monthly</option>
          </select>
        </Field>
      </div>
      <Field label="Depends on">
        <MultiSelect options={existingTasks.map((t) => ({ value: t.id, label: t.title }))} selected={dependsOn} onChange={setDependsOn} />
      </Field>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
        <button style={S.btnGhost} onClick={onClose}>Cancel</button>
        <button style={S.btnPrimary} disabled={saving} onClick={save}>Create task</button>
      </div>
    </Modal>
  );
}

// ---- shared bits -----------------------------------------------------------
function Tab({ active, onClick, children }) { return <button onClick={onClick} style={{ ...S.tab, ...(active ? S.tabActive : {}) }}>{children}</button>; }
function Field({ label, children }) { return <label style={S.field}><span style={S.fieldLabel}>{label}</span>{children}</label>; }
function StatusPill({ status }) { return <span style={{ ...S.pill, background: STATUS_COLORS[status] }}>{status}</span>; }
function Modal({ title, onClose, children }) {
  return (
    <div style={S.drawerOverlay} onClick={onClose}>
      <div style={S.modal} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
          <h3 style={{ margin: 0, color: KA.navy }}>{title}</h3>
          <button style={S.closeBtn} onClick={onClose}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}
function MultiSelect({ options, selected, onChange }) {
  const toggle = (v) => onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  return (
    <div style={S.multi}>
      {options.length === 0 && <span style={{ color: '#aaa', fontSize: 13 }}>No options</span>}
      {options.map((o) => (
        <span key={o.value} onClick={() => toggle(o.value)} style={{ ...S.chip, ...(selected.includes(o.value) ? S.chipOn : {}) }}>{o.label}</span>
      ))}
    </div>
  );
}
// Dropdown with tick boxes, alphabetized by first name, plus Select all + search.
// Expands inline (the modal scrolls) so it can't get clipped by the overlay.
function CheckboxDropdown({ options, selected, onChange, placeholder = 'Select people…' }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const firstName = (label) => (label || '').trim().split(/\s+/)[0].toLowerCase();
  const sorted = useMemo(
    () => [...options].sort((a, b) => firstName(a.label).localeCompare(firstName(b.label)) || (a.label || '').localeCompare(b.label || '')),
    [options]
  );
  const filtered = q ? sorted.filter((o) => o.label.toLowerCase().includes(q.toLowerCase())) : sorted;
  const toggle = (v) => onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  const shownValues = filtered.map((o) => o.value);
  const allShownSelected = shownValues.length > 0 && shownValues.every((v) => selected.includes(v));
  const toggleAll = () => {
    if (allShownSelected) onChange(selected.filter((v) => !shownValues.includes(v)));
    else onChange(Array.from(new Set([...selected, ...shownValues])));
  };
  const buttonLabel = selected.length === 0 ? placeholder
    : selected.length === 1 ? (options.find((o) => o.value === selected[0])?.label || '1 selected')
    : `${selected.length} selected`;
  return (
    <div>
      <button type="button" style={S.ddButton} onClick={() => setOpen((o) => !o)}>
        <span style={{ color: selected.length ? '#222' : '#999' }}>{buttonLabel}</span>
        <span style={{ color: '#999' }}>{open ? '▴' : '▾'}</span>
      </button>
      {open && (
        <div style={S.ddPanel}>
          <input autoFocus style={{ ...S.select, marginBottom: 8 }} placeholder="Search by name…"
            value={q} onChange={(e) => setQ(e.target.value)} />
          <label style={{ ...S.ddRow, fontWeight: 600, borderBottom: '1px solid #eee' }}>
            <input type="checkbox" checked={allShownSelected} onChange={toggleAll} />
            <span>Select all{q ? ' (matches)' : ''}</span>
          </label>
          <div style={{ maxHeight: 240, overflowY: 'auto' }}>
            {filtered.length === 0 && <div style={{ padding: 8, color: '#aaa', fontSize: 13 }}>No matches</div>}
            {filtered.map((o) => (
              <label key={o.value} style={S.ddRow}>
                <input type="checkbox" checked={selected.includes(o.value)} onChange={() => toggle(o.value)} />
                <span>{o.label}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
function fmtTs(ts) {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return new Intl.DateTimeFormat('en-GB', { timeZone: 'Asia/Amman', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }).format(d);
}
function dueTone(tone) {
  switch (tone) {
    case 'over': return { background: '#FDECEA', color: KA.red };
    case 'today': return { background: '#FEF3E2', color: '#B7791F' };
    case 'soon': return { background: '#EAF2FD', color: '#2F6FED' };
    default: return { background: '#F1F3F5', color: '#777' };
  }
}

const S = {
  wrap: { maxWidth: 1200, margin: '0 auto', padding: '20px 24px', fontFamily: 'system-ui, sans-serif' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  h1: { color: KA.navy, margin: 0, fontSize: 26 }, sub: { color: '#777', fontSize: 14 },
  h2: { color: KA.navy, fontSize: 18, marginTop: 0 },
  tabs: { display: 'flex', gap: 4, borderBottom: '2px solid #eee', marginBottom: 16 },
  tab: { padding: '10px 18px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 15, color: '#666', borderBottom: '2px solid transparent', marginBottom: -2 },
  tabActive: { color: KA.red, borderBottom: `2px solid ${KA.red}`, fontWeight: 600 },
  panel: { background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
  projectBar: { display: 'flex', gap: 10, alignItems: 'center', marginBottom: 14 },
  select: { padding: '8px 10px', borderRadius: 8, border: '1px solid #ccc', fontSize: 14, width: '100%', boxSizing: 'border-box' },
  miniSelect: { padding: '4px 6px', borderRadius: 6, border: '1px solid #ccc', fontSize: 12 },
  btnPrimary: { background: KA.red, color: '#fff', border: 'none', padding: '9px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap' },
  btnGhost: { background: '#fff', color: KA.navy, border: `1px solid ${KA.navy}`, padding: '9px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 14, whiteSpace: 'nowrap' },
  board: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginTop: 12 },
  column: { background: '#F7F8FA', borderRadius: 10, padding: 10, minHeight: 120 },
  colHead: { fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8, display: 'flex', justifyContent: 'space-between' },
  colCount: { background: '#fff', borderRadius: 10, padding: '0 8px', color: '#666' },
  card: { background: '#fff', borderRadius: 8, padding: 10, marginBottom: 8, cursor: 'pointer', boxShadow: '0 1px 2px rgba(0,0,0,0.06)' },
  prio: { color: '#fff', fontSize: 10, padding: '2px 6px', borderRadius: 6, fontWeight: 700 },
  due: { fontSize: 11, padding: '2px 8px', borderRadius: 10, fontWeight: 600, whiteSpace: 'nowrap' },
  blockedNote: { fontSize: 11, color: KA.red, marginTop: 6 }, fileChip: { fontSize: 11, color: '#666', marginTop: 4 },
  myRow: { display: 'flex', alignItems: 'center', gap: 12, padding: '12px 8px', borderBottom: '1px solid #f0f0f0', cursor: 'pointer' },
  pill: { color: '#fff', fontSize: 11, padding: '3px 10px', borderRadius: 12, fontWeight: 600 },
  progressWrap: { flex: 1, height: 8, background: '#eee', borderRadius: 4, overflow: 'hidden', maxWidth: 240 },
  progressBar: { height: '100%', background: '#27AE60' },
  ovRow: { display: 'flex', alignItems: 'center', gap: 16, padding: '14px 8px', borderBottom: '1px solid #f0f0f0', cursor: 'pointer' },
  ovStat: { fontSize: 13, color: '#555', minWidth: 70, textAlign: 'right' },
  drawerOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', justifyContent: 'flex-end', zIndex: 1000 },
  drawer: { width: 'min(560px, 100%)', height: '100%', background: '#fff', padding: 24, overflowY: 'auto', boxShadow: '-2px 0 12px rgba(0,0,0,0.15)' },
  modal: { width: 'min(560px, 92%)', background: '#fff', borderRadius: 12, padding: 24, margin: 'auto', maxHeight: '90vh', overflowY: 'auto' },
  titleInput: { fontSize: 20, fontWeight: 700, color: KA.navy, border: 'none', borderBottom: '2px solid transparent', flex: 1, outline: 'none' },
  closeBtn: { background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#999' },
  warnBanner: { background: '#FDECEA', color: KA.red, padding: '8px 12px', borderRadius: 8, fontSize: 13, margin: '12px 0' },
  fieldGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 },
  field: { display: 'block', marginTop: 12 }, fieldLabel: { display: 'block', fontSize: 12, fontWeight: 600, color: '#666', marginBottom: 4 },
  textarea: { width: '100%', minHeight: 70, padding: 10, borderRadius: 8, border: '1px solid #ccc', fontSize: 14, boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical' },
  sectionLabel: { fontSize: 13, fontWeight: 700, color: KA.navy, marginBottom: 8, borderBottom: '1px solid #eee', paddingBottom: 4 },
  fileRow: { display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0' },
  comment: { background: '#F7F8FA', borderRadius: 8, padding: 10, marginBottom: 8 },
  linkDanger: { background: 'none', border: 'none', color: KA.red, cursor: 'pointer', fontSize: 13 },
  linkBtn: { background: 'none', border: 'none', color: KA.navy, cursor: 'pointer', fontSize: 13 },
  multi: { display: 'flex', flexWrap: 'wrap', gap: 6 },
  chip: { padding: '4px 10px', borderRadius: 14, border: '1px solid #ccc', fontSize: 12, cursor: 'pointer', background: '#fff', color: '#555' },
  chipOn: { background: KA.navy, color: '#fff', borderColor: KA.navy },
  ddButton: { width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', borderRadius: 8, border: '1px solid #ccc', background: '#fff', fontSize: 14, cursor: 'pointer', boxSizing: 'border-box' },
  ddPanel: { marginTop: 6, background: '#fff', border: '1px solid #ddd', borderRadius: 8, padding: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  ddRow: { display: 'flex', alignItems: 'center', gap: 8, padding: '6px 4px', fontSize: 14, cursor: 'pointer' },
  tag: { color: '#fff', fontSize: 10, padding: '2px 8px', borderRadius: 8, fontWeight: 700 },
  bell: { position: 'relative', background: 'none', border: 'none', fontSize: 22, cursor: 'pointer' },
  badge: { position: 'absolute', top: -4, right: -6, background: KA.red, color: '#fff', fontSize: 10, borderRadius: 10, padding: '1px 5px', fontWeight: 700 },
  bellPanel: { position: 'absolute', right: 0, top: 34, width: 320, background: '#fff', borderRadius: 10, boxShadow: '0 4px 16px rgba(0,0,0,0.18)', zIndex: 1100, maxHeight: 400, overflowY: 'auto' },
  notifRow: { padding: 12, borderBottom: '1px solid #f0f0f0', cursor: 'pointer', fontSize: 13 },
};